/**
 * @fileoverview
 *
 * Functions to initiate and control `_fwn.widgets`
 * SDK attribute which provides programmatic access
 * to all available widgets on a scene. Widgets are
 * accessible by a `widget_id` and `name` key.
 */

import { customEvent } from '@core/customEvent/customEvent'
import { EventTypeReady } from '@core/customEvent/eventTypeReady'
import { getFWN } from '@core/fwn'
import type { WidgetTypesEnum } from '@core/types'

import type { IWidgetInstance, IWidgets } from './types'
import { WidgetControl } from './WidgetControl'

const allWidgetsCache: IWidgets = Object.create(null)

/**
 * Getter to access current module level cache
 */
export function fwnWidgetsGetter() {
  return allWidgetsCache
}

/**
 * Register a widget instance under certain keys (widget_id, name).
 */
export function registerFWNWidget(
  widgetId: string,
  name: string,
  type: WidgetTypesEnum,
  instance: IWidgetInstance,
) {
  const fwn = getFWN()

  if (fwn) {
    const widget = new WidgetControl(instance)

    allWidgetsCache[widgetId] = widget
    if (name) {
      allWidgetsCache[name] = widget
    }

    customEvent({
      type: EventTypeReady.FW_WIDGET_READY,
      data: { widgetId, name, type, widget },
    })

    return widget
  }
}

/**
 * Unregister - destroy a widget instance.
 */
export function unregisterFWNWidget(widgetId: string, name: string) {
  const fwn = getFWN()
  if (fwn) {
    delete allWidgetsCache[widgetId]
    if (name) {
      delete allWidgetsCache[name]
    }
  }
}
